import { getAuthRefreshToken } from '@api/authApiHandler';
import { getToken, setToken, updateUserDetails } from '@api/config';
import { createHeaders } from '@api/httpHeaderConfig';
import { logoutUser } from '@helpers/auth.helper';
import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { HttpStatusCode } from './httpStatusCodeConfig';

// Define proper function types
type ResolveFunction = (value: string | null) => void;
type RejectFunction = (reason?: any) => void;

interface QueueItem {
  resolve: ResolveFunction;
  reject: RejectFunction;
  timestamp: number;
}

// Shared token refresh state management
let isRefreshing = false;
let refreshStartTime: number | null = null;
let failedQueue: QueueItem[] = [];

const REFRESH_TIMEOUT = 5000; // 5 seconds max wait for refresh
const MAX_QUEUE_WAIT = 6000; // 6 seconds max wait in queue

// Helper function to check if token is valid
const isValidToken = (token: string | null): boolean =>
  token !== null && token !== undefined && token !== 'undefined' && token.trim() !== '';

// Consolidated token refresh function
const refreshToken = async (): Promise<string | null> => {
  try {
    const response = await getAuthRefreshToken();

    const result = response;

    if (result && result?.token) {
      setToken(result.token);
    }

    if (result && result?.user && result?.userDetails) {
      updateUserDetails(result?.user, result?.userDetails);
    }

    if (result && result?.token) {
      return result.token;
    }

    return null;
  } catch (error) {
    console.error('Token refresh failed:', error);
    // If refresh fails, logout the user
    logoutUser();

    return null;
  }
};

const processQueue = (error: any, token: string | null = null): void => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

// Clean up expired queue items
const cleanExpiredQueue = (): void => {
  const now = Date.now();
  const validQueue = failedQueue.filter((item) => {
    const isExpired = now - item.timestamp > MAX_QUEUE_WAIT;
    if (isExpired) {
      item.reject(new Error('Queue timeout'));
    }

    return !isExpired;
  });
  failedQueue = validQueue;
};

// Consolidated token refresh handler
const handleTokenRefresh = async (): Promise<string | null> => {
  if (isRefreshing) {
    // Check if refresh has been running too long
    if (refreshStartTime && Date.now() - refreshStartTime > REFRESH_TIMEOUT) {
      logoutUser();
    }

    // Clean expired items from queue
    cleanExpiredQueue();

    // If already refreshing, queue this request
    return new Promise<string | null>((resolve, reject) => {
      failedQueue.push({
        resolve,
        reject,
        timestamp: Date.now(),
      });
    }).catch((err) => {
      if (err.message === 'Queue timeout') {
        logoutUser();
      }

      throw err;
    });
  }

  isRefreshing = true;
  refreshStartTime = Date.now();

  // Set up a timeout for the refresh operation
  const refreshTimeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => {
      reject(new Error('Refresh operation timeout'));
    }, REFRESH_TIMEOUT);
  });

  try {
    // Race the refresh against the timeout
    const newToken = await Promise.race([refreshToken(), refreshTimeoutPromise]);

    processQueue(null, newToken);

    return newToken;
  } catch (refreshError) {
    processQueue(refreshError, null);
    logoutUser();

    throw refreshError;
  } finally {
    isRefreshing = false;
    refreshStartTime = null;
  }
};

const createApiClient = (baseURL = process.env.REACT_APP_ACTION_PLAN_API_URL): AxiosInstance => {
  const apiClient: AxiosInstance = axios.create({
    baseURL,
    timeout: 120000,
    headers: createHeaders(),
    withCredentials: true,
  });

  // Request interceptor for dynamic token injection
  apiClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = getToken();
      if (isValidToken(token)) {
        config.headers.set('Authorization', `Bearer ${token}`);
      }

      return config;
    },
    (error) => Promise.reject(error.response?.data ?? error.message),
  );

  // Response interceptor for extracting data and handling errors
  apiClient.interceptors.response.use(
    (response: AxiosResponse) => response.data,
    async (error) => {
      const originalRequest = error.config;

      // Check if the error is due to unauthorized access (401) or invalid token
      const currentToken = getToken();
      const shouldRefreshToken =
        (error.response?.status === HttpStatusCode.UNAUTHORIZED || !isValidToken(currentToken)) &&
        !originalRequest.retryAttempted;

      if (shouldRefreshToken) {
        originalRequest.retryAttempted = true;

        try {
          const newToken = await handleTokenRefresh();

          if (!newToken && !isValidToken(newToken)) {
            logoutUser();
          }

          if (newToken && isValidToken(newToken)) {
            // Retry the original request with new token
            originalRequest.headers.Authorization = `Bearer ${newToken}`;

            return apiClient(originalRequest);
          }

          // else {
          //   throw new Error('Failed to obtain valid token');
          // }
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }

      // Check if the error is due to forbidden access (403)
      if (error.response?.status === HttpStatusCode.FORBIDDEN) {
        logoutUser();
      }

      // Continue rejecting the promise with the error data
      return Promise.reject(error.response?.data ?? error.message);
    },
  );

  return apiClient;
};

export default createApiClient;
export { handleTokenRefresh, isValidToken };
